import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  CognitoAuthService,
  CognitoConfig,
  CognitoAuthState,
  IdTokenPayload,
} from "./cognitoAuth";

interface CognitoAuthContextType extends CognitoAuthState {
  cognitoAuth: CognitoAuthService;
  signInWithIdP: (idpIdentifier: string) => Promise<void>;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  handleCallback: (code: string, state: string) => Promise<void>;
  getIdToken: () => Promise<string | null>;
  recheckAuth: () => Promise<void>;
}

const CognitoAuthContext = createContext<CognitoAuthContextType | undefined>(
  undefined
);

interface CognitoAuthProviderProps {
  config: CognitoConfig;
  children: React.ReactNode;
}

export const CognitoAuthProvider: React.FC<CognitoAuthProviderProps> = ({
  config,
  children,
}) => {
  const [cognitoAuth] = useState(() => new CognitoAuthService(config));
  const [authState, setAuthState] = useState<CognitoAuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  });

  // Check authentication status on mount
  const checkAuth = useCallback(async () => {
    try {
      const idToken = await cognitoAuth.getIdToken();

      if (idToken) {
        const parsedToken = cognitoAuth.parseIdToken(idToken);
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: parsedToken,
          error: null,
        });

        // Start auto-refresh (only works for OAuth tokens with refresh token)
        cognitoAuth.startRefreshTimer();
      } else {
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: null,
        });
      }
    } catch (error) {
      console.error("Auth check failed", error);
      setAuthState({
        isAuthenticated: false,
        isLoading: false,
        user: null,
        error: error instanceof Error ? error : new Error(String(error)),
      });
    }
  }, [cognitoAuth]);

  useEffect(() => {
    checkAuth();

    // Listen for custom auth completion events
    const handleAuthUpdate = () => {
      checkAuth();
    };

    window.addEventListener("cognito-auth-updated", handleAuthUpdate);

    return () => {
      cognitoAuth.stopRefreshTimer();
      window.removeEventListener("cognito-auth-updated", handleAuthUpdate);
    };
  }, [cognitoAuth, checkAuth]);

  const signInWithIdP = useCallback(
    async (idpIdentifier: string) => {
      await cognitoAuth.signInWithIdP(idpIdentifier);
    },
    [cognitoAuth]
  );

  const signIn = useCallback(async () => {
    await cognitoAuth.signIn();
  }, [cognitoAuth]);

  const signOut = useCallback(async () => {
    await cognitoAuth.signOut();
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    });
  }, [cognitoAuth]);

  const handleCallback = useCallback(
    async (code: string, state: string) => {
      try {
        setAuthState((prev) => ({ ...prev, isLoading: true }));

        const tokens = await cognitoAuth.handleCallback(code, state);
        const parsedToken = cognitoAuth.parseIdToken(tokens.idToken);

        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          user: parsedToken,
          error: null,
        });

        // Start auto-refresh
        cognitoAuth.startRefreshTimer();
      } catch (error) {
        console.error("Callback handling failed", error);
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: error instanceof Error ? error : new Error(String(error)),
        });
      }
    },
    [cognitoAuth]
  );

  const getIdToken = useCallback(async () => {
    return cognitoAuth.getIdToken();
  }, [cognitoAuth]);

  const value: CognitoAuthContextType = {
    ...authState,
    cognitoAuth,
    signInWithIdP,
    signIn,
    signOut,
    handleCallback,
    getIdToken,
    recheckAuth: checkAuth,
  };

  return (
    <CognitoAuthContext.Provider value={value}>
      {children}
    </CognitoAuthContext.Provider>
  );
};

export const useCognitoAuth = (): CognitoAuthContextType => {
  const context = useContext(CognitoAuthContext);

  if (context === undefined) {
    throw new Error("useCognitoAuth must be used within CognitoAuthProvider");
  }

  return context;
};
